/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useEffect } from "react";
import { FiX, FiTrash2, FiMinus, FiPlus, FiShoppingBag, FiMapPin, FiPhone, FiUser, FiTag, FiEdit3 } from "react-icons/fi";
import { buildImageUrl } from "../theme1/utils/theme1Helpers";
import { formatNumber } from "../../utils/formatNumber";

const CartSidebar = ({
  theme,
  cities,
  cart,
  appliedCoupon,
  couponDiscount,
  subtotal,
  shipping,
  total,
  displayCoupons,
  couponCode,
  setCouponCode,
  couponError,
  applyCouponCode,
  handleApplyCoupon,
  handleUpdateQty,
  handleRemove,
  deliveryForm,
  setDeliveryForm,
  handleCheckoutSubmit,
  isSubmitting = false,
  onClose,
}) => {
  useEffect(() => {
    if (deliveryForm?.cityId) return;
    const savedCityId = localStorage.getItem("cartSavedCityId") || localStorage.getItem("selectedCityId");
    const savedCityName = localStorage.getItem("cartSavedCityName");
    if (savedCityId) {
      setDeliveryForm((prev) => ({
        ...prev,
        cityId: savedCityId,
        cityName: savedCityName || prev.cityName || "",
      }));
    }
  }, [deliveryForm?.cityId, setDeliveryForm]);

  const fieldClass = "t3-cart-input";

  return (
    <div className="t3-cart" dir="rtl">
      <div className="t3-cart-head">
        <div className="t3-cart-title">
          <span className="t3-cart-ico" style={{ background: theme.primary }}>
            <FiShoppingBag />
          </span>
          <div>
            <h2>سلة المشتريات</h2>
            <p>{cart.reduce((sum, item) => sum + (Number(item.quantity || item.qty) || 0), 0)} منتج</p>
          </div>
        </div>
        <button type="button" className="t3-icon-btn" onClick={onClose} aria-label="إغلاق">
          <FiX />
        </button>
      </div>

      <div className="t3-cart-body">
        {cart.length === 0 ? (
          <div className="t3-cart-empty">
            <FiShoppingBag />
            <h3>سلتك فارغة حالياً</h3>
            <button type="button" onClick={onClose} style={{ background: theme.primary }}>ابدأ التسوق</button>
          </div>
        ) : (
          <div className="t3-cart-layout">
            <div className="t3-cart-items">
              {cart.map((item, idx) => {
                const v = item.variants?.[0] || item;
                const itemName = item.itemName || item.name || "منتج";
                const qty = Number(v.quantity || item.quantity || 1);
                const price = Number(v.price || item.price || 0);
                const colorName = v.color || item.color || null;
                const colorHex = typeof colorName === "string" && colorName.startsWith("#") ? colorName : null;
                const sizeValue = v.size || item.size || "";
                const itemImage =
                  v.imageUrl || item.imageUrl || item.parentImageUrl || item.mainImage || item.mainImageUrl || item.image;

                return (
                  <div key={`cart-item-${v.variantId || idx}-${idx}`} className="t3-cart-item">
                    <img src={buildImageUrl(itemImage)} alt={itemName} />
                    <div className="t3-cart-item-info">
                      <div className="t3-cart-item-top">
                        <h4>{itemName}</h4>
                        <button type="button" onClick={() => handleRemove(v.variantId || item.variantId)}>
                          <FiTrash2 />
                        </button>
                      </div>
                      <div className="t3-cart-meta">
                        {colorName && (
                          <span>
                            اللون
                            {colorHex ? <i style={{ background: colorHex }} /> : `: ${colorName}`}
                          </span>
                        )}
                        {sizeValue && <span>المقاس: {sizeValue}</span>}
                      </div>
                      <div className="t3-cart-item-bottom">
                        <div className="t3-qty">
                          <button type="button" onClick={() => handleUpdateQty(v.variantId || item.variantId, -1)}><FiMinus /></button>
                          <b>{qty}</b>
                          <button type="button" onClick={() => handleUpdateQty(v.variantId || item.variantId, 1)}><FiPlus /></button>
                        </div>
                        <strong style={{ color: theme.primary }}>{formatNumber(price * qty)} د.ع</strong>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="t3-cart-side">
              <div className="t3-cart-card">
                <h3><FiMapPin style={{ color: theme.primary }} /> معلومات التوصيل</h3>
                <label>
                  الاسم
                  <span className={fieldClass}>
                    <FiUser />
                    <input
                      type="text"
                      placeholder="الاسم الكامل"
                      value={deliveryForm.name || ""}
                      onChange={(e) => setDeliveryForm({ ...deliveryForm, name: e.target.value })}
                    />
                  </span>
                </label>
                <label>
                  رقم الهاتف
                  <span className={fieldClass}>
                    <FiPhone />
                    <input
                      type="tel"
                      dir="ltr"
                      placeholder="07xxxxxxxx"
                      value={deliveryForm.phone || ""}
                      onChange={(e) => setDeliveryForm({ ...deliveryForm, phone: e.target.value.replace(/\D/g, "") })}
                    />
                  </span>
                </label>
                <label>
                  المحافظة
                  <span className={fieldClass}>
                    <FiMapPin />
                    <select
                      value={deliveryForm.cityId ? String(deliveryForm.cityId) : ""}
                      onChange={(e) => {
                        const selected = (cities || []).find((c) => String(c.cityId) === String(e.target.value));
                        setDeliveryForm((prev) => ({
                          ...prev,
                          cityId: e.target.value,
                          cityName: selected?.cityName || prev.cityName || "",
                        }));
                        localStorage.setItem("cartSavedCityId", e.target.value);
                        localStorage.setItem("cartSavedCityName", selected?.cityName || "");
                        localStorage.setItem("selectedCityId", e.target.value);
                      }}
                    >
                      <option value="">اختر المحافظة</option>
                      {(cities || []).map((city) => (
                        <option key={city.cityId} value={String(city.cityId)}>{city.cityName}</option>
                      ))}
                    </select>
                  </span>
                </label>
                <label>
                  عنوان التوصيل
                  <textarea
                    rows="2"
                    placeholder="المنطقة، أقرب نقطة دالة..."
                    value={deliveryForm.address || ""}
                    onChange={(e) => setDeliveryForm({ ...deliveryForm, address: e.target.value })}
                  />
                </label>
                <label>
                  الملاحظات
                  <span className={fieldClass}>
                    <FiEdit3 />
                    <input
                      type="text"
                      placeholder="أي ملاحظة للتوصيل (اختياري)"
                      value={deliveryForm.notes || ""}
                      onChange={(e) => setDeliveryForm({ ...deliveryForm, notes: e.target.value })}
                    />
                  </span>
                </label>
              </div>

              <div className="t3-cart-card">
                <h3><FiTag style={{ color: theme.primary }} /> كوبونات الخصم</h3>
                <form onSubmit={handleApplyCoupon} className="t3-coupon-row">
                  <input
                    type="text"
                    value={couponCode || ""}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="أدخل كود الخصم"
                    disabled={!!appliedCoupon}
                  />
                  <button type="submit" disabled={!!appliedCoupon || !(couponCode?.trim())} style={{ background: theme.primary }}>
                    تطبيق
                  </button>
                </form>
                {couponError && <p className="t3-err">{couponError}</p>}
                {!appliedCoupon && displayCoupons?.length > 0 && (
                  <div className="t3-coupon-chips">
                    {displayCoupons.slice(0, 3).map((c) => (
                      <button key={c.id || c.code} type="button" onClick={(e) => { e.preventDefault(); applyCouponCode ? applyCouponCode(c.code) : setCouponCode(c.code); }}>
                        {c.code}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="t3-cart-card t3-summary">
                <span className="t3-summary-bar" style={{ background: theme.primary }} />
                <h3>ملخص الطلب</h3>
                <div className="t3-sum-row"><span>المجموع الفرعي</span><b>{formatNumber(subtotal)} د.ع</b></div>
                {appliedCoupon && (
                  <div className="t3-sum-row is-ok"><span>الخصم</span><b>-{formatNumber(couponDiscount)} د.ع</b></div>
                )}
                <div className="t3-sum-row">
                  <span>التوصيل</span>
                  <b>
                    {!deliveryForm.cityId ? "يرجى اختيار المحافظة" : shipping === 0 ? "مجاني" : `${formatNumber(shipping)} د.ع`}
                  </b>
                </div>
                <div className="t3-sum-total">
                  <span>الإجمالي</span>
                  <strong style={{ color: theme.primary }}>{formatNumber(total)} د.ع</strong>
                </div>
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={(e) => {
                    e.preventDefault();
                    handleCheckoutSubmit(e);
                  }}
                  style={{ background: theme.primary }}
                >
                  {isSubmitting ? "جاري إرسال الطلب..." : "إتمام الطلب"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartSidebar;
